import { IconButton, LinearProgress, TableBody, TableCell } from '@material-ui/core';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import Loader from 'Components/Common/Loader';
import { MainMenuListItemSubItem } from 'Components/Layout/MainMenuListItem';
import ChangeUserPasswordModal from 'Components/Users/ChangeUserPasswordModal';
import CreateUserModal from 'Components/Users/CreateUserModal';
import DeleteUserModal from 'Components/Users/DeleteUserModal';
import useFetchUsers from 'Hooks/Http/Users/useFetchUsers';
import useAppSelector from 'Hooks/Redux/useAppSelector';
import useMessagesDispatch from 'Hooks/Redux/useMessagesDispatch';
import Layout from 'Layout';
import { UserResponse } from 'Models/User';
import { ReactElement, useState } from 'react';
import putUser from 'Utils/Http/Requests/Users/PutUserRequest';

const UsersView = (): ReactElement => {
	const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
	const [editingUser, setEditingUser] = useState<UserResponse | null>(null);
	const [deletingUser, setDeletingUser] = useState<UserResponse | null>(null);
	const [passwordChangingUser, setPasswordChangingUser] = useState<UserResponse | null>(null);

	const usersData = useFetchUsers();
	const token = useAppSelector((x) => x.profile.token);
	const { dispatchSetAxiosErrorMessage } = useMessagesDispatch();

	const handleCreateNewClicked = (): void => {
		setIsCreateModalOpen(true);
	};

	const handleModalClose = (): void => {
		setIsCreateModalOpen(false);
		setEditingUser(null);
	};

	const layoutSubItems: MainMenuListItemSubItem[] = [{ primary: 'Create New', onClick: handleCreateNewClicked }];

	const addNewUserToTable = (user: UserResponse): void => {
		usersData.setData((prev) => {
			if (prev === null) return { users: [user] };
			return { users: [...prev.users, user] };
		});
	};

	const updateUserInTable = (user: UserResponse): void => {
		usersData.setData((prev) => {
			if (prev === null) return null;
			const idx = prev.users.findIndex((x) => x.id === user.id);
			if (idx < 0) return prev;
			const copiedPrevUsers = [...prev.users];
			copiedPrevUsers[idx] = user;
			return { users: copiedPrevUsers };
		});
	};

	const handleUserCreated = (isCreate: boolean, user: UserResponse): void => {
		if (isCreate) addNewUserToTable(user);
		else updateUserInTable(user);
	};

	const handleToggleUserEnabled = async (user: UserResponse): Promise<void> => {
		const newUser = { ...user, enabled: !user.enabled };
		try {
			const response = await putUser(token, newUser, newUser.id);
			updateUserInTable(response.data.user);
		} catch (e) {
			dispatchSetAxiosErrorMessage(e);
		}
	};

	const handleEditButtonClicked = (user: UserResponse): void => {
		setEditingUser(user);
		setIsCreateModalOpen(true);
	};

	const handleDeleteButtonClicked = (user: UserResponse): void => {
		setDeletingUser(user);
	};

	const HandleChangePasswordButtonClicked = async (user: UserResponse): Promise<void> => {
		setPasswordChangingUser(user);
	};

	const handleUserDeleted = () => {
		setDeletingUser(null);
		usersData.setData((prev) => {
			if (prev === null) return null;
			return { users: prev.users.filter((x) => x.id !== deletingUser?.id) };
		});
	};

	return (
		<Layout title="Users" subItems={layoutSubItems}>
			<Loader isLoading={usersData.loading} loaderComponent={<LinearProgress />}>
				<TableContainer component={Paper}>
					<Table>
						<TableHead>
							<TableRow>
								<TableCell>ID</TableCell>
								<TableCell>Name</TableCell>
								<TableCell>Email</TableCell>
								<TableCell>Enabled</TableCell>
								<TableCell>Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{usersData.data?.users.map((user) => (
								<TableRow key={user.id}>
									<TableCell>{user.id}</TableCell>
									<TableCell>{user.name}</TableCell>
									<TableCell>{user.email}</TableCell>
									<TableCell onClick={() => handleToggleUserEnabled(user)}>
										{user.enabled ? <CheckIcon color="primary" /> : <CloseIcon color="error" />}
									</TableCell>
									<TableCell>
										<IconButton onClick={() => handleEditButtonClicked(user)}>
											<EditIcon />
										</IconButton>
										<IconButton onClick={() => handleDeleteButtonClicked(user)}>
											<DeleteIcon />
										</IconButton>
										<IconButton onClick={() => HandleChangePasswordButtonClicked(user)}>
											<VpnKeyIcon />
										</IconButton>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			</Loader>
			<CreateUserModal
				isOpen={isCreateModalOpen}
				onClose={handleModalClose}
				onSave={handleUserCreated}
				user={editingUser}
			/>
			<DeleteUserModal
				isOpen={deletingUser !== null}
				onClose={() => setDeletingUser(null)}
				user={deletingUser}
				onSuccess={handleUserDeleted}
			/>
			<ChangeUserPasswordModal
				isOpen={passwordChangingUser !== null}
				onClose={() => setPasswordChangingUser(null)}
				user={passwordChangingUser}
			/>
		</Layout>
	);
};

export default UsersView;
